import {BarChart, Delete, Edit, MoreHoriz} from '@mui/icons-material'
import {
  Stack,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Grid,
} from '@mui/material'
import React from 'react'
import paths from '../../../routes/paths'
import {useNavigate} from 'react-router-dom'
import surveysApi from '../../../services/api/surveys/surveysApi'
import WbAlert from '../../../components/common/WbAlert'
import ResendInvites from './ResendInvites'
import WbModal from '../../../components/common/WbModal'
import GroupCyclesList from './GroupCyclesList'
import ParticipantCount from './ParticipantCount'
import WbOutlineButton from '../../../components/common/WbOutlineButton'
import WbButtom from '../../../components/common/WbButton'
import ResendInviteTabs from './ResendInviteTabs'
import PersonOutlineSharpIcon from '@mui/icons-material/PersonOutlineSharp'
import SurveyQuestions from './QuestionList/SurveyQuestions'
import ChangeEndDate from './ChangeEndDate'
import AddSchool from './AddSchool'
import {eiSurveyUrl} from '../../../utils/eiLinks'

export default function SurveyCard({
  survey,
  updateListOnDelete,
  loadSurveys,
  canDoActions,
  canChangeEndDate,
  cType,
}) {
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [showResendInvites, setShowResendInvites] = React.useState(false)
  const [deleteConfirm, setDeleteConfirm] = React.useState(false)
  const [showSurveyQuestions, setShowSurveyQuestions] = React.useState(false)
  const [showChangeEndDate, setShowChangeEndDate] = React.useState(false)
  const [showAddSchool, setShowAddSchool] = React.useState(false)
  const isMatSurvey = survey?.matId > 0
  const handleClose = event => {
    setAnchorEl(null)
  }
  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }
  const getAudienceChip = audience => {
    switch (audience) {
      case 1:
        return <Chip className="audience-chip" label={'Staff'} size="small" />
      case 2:
        return <Chip className="audience-chip" label={'Parents'} size="small" />
      case 3:
        return (
          <>
            <Chip className="audience-chip" label={'Staff'} size="small" />
            <Chip className="audience-chip" label={'Parent'} size="small" />
          </>
        )
      case 4:
        return (
          <Chip className="audience-chip" label={'Students'} size="small" />
        )
      case 5:
        return (
          <>
            <Chip className="audience-chip" label={'Students'} size="small" />
            <Chip className="audience-chip" label={'Staff'} size="small" />
          </>
        )
      case 6:
        return (
          <>
            <Chip className="audience-chip" label={'Students'} size="small" />
            <Chip className="audience-chip" label={'Parents'} size="small" />
          </>
        )
      default:
        return (
          <>
            <Chip className="audience-chip" label={'Staff'} size="small" />
            <Chip className="audience-chip" label={'Students'} size="small" />
            <Chip className="audience-chip" label={'Parents'} size="small" />
          </>
        )
    }
  }

  const deleteSurvey = async () => {
    setDeleteConfirm(false)
    setAnchorEl(null)
    try {
      // Only touch the UI once the server confirms the delete committed. The API
      // returns true when the survey was soft-deleted and false when the token
      // matched nothing. This used to be fire-and-forget - the card was removed
      // and "deleted successfully" shown regardless - so a delete that never
      // committed (timeout, error, or no match) left the row in the database and
      // the survey reappeared on the next dashboard load (WI-424).
      const deleted = await surveysApi.delete(survey.token)
      if (deleted === true) {
        updateListOnDelete(survey.id)
        WbAlert({message: 'Survey deleted successfully', type: 'success'})
      } else {
        // A non-true result means the API found no survey for this token
        // (DeleteSurveyByTokenQuery returns false when the row is null) - it was
        // already deleted or no longer exists, so retrying won't help. Re-sync
        // from the server so the stale card is reconciled.
        WbAlert({
          message:
            'This survey could not be found - it may already have been deleted. Refreshing your list.',
          type: 'warning',
        })
        loadSurveys()
      }
    } catch (error) {
      WbAlert({
        message: error?.timedOut
          ? 'The delete request timed out - refreshing to check whether it went through.'
          : 'Survey could not be deleted. Please try again.',
        type: 'error',
      })
      // Re-sync from the server so a survey that was not actually deleted does
      // not silently disappear from the list.
      loadSurveys()
    }
  }

  const getTag = tag => {
    if (tag?.length > 20) {
      return tag.slice(0, 20) + '...'
    }
    return tag
  }

  return (
    <div className="surveys">
      {deleteConfirm && (
        <WbModal
          title="Delete Survey"
          onClose={e => setDeleteConfirm(false)}
          content={
            <Grid container spacing={2}>
              <Grid item xs={12}>
                Are you sure you want to delete this survey?
              </Grid>
              <Grid
                item
                xs={12}
                style={{
                  display: 'flex',
                  textAlign: 'right',
                  justifyContent: 'flex-end',
                }}
                spacing={2}
              >
                <WbOutlineButton
                  CustomOutlineButtonText={'Cancel'}
                  onClick={e => setDeleteConfirm(false)}
                />
                &nbsp; &nbsp;
                <WbButtom CustomButtonText={'Delete'} onClick={deleteSurvey} />
              </Grid>
            </Grid>
          }
        ></WbModal>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Stack direction={'column'}>
          <div className="surveyname">
            {survey?.name.length > 30
              ? survey?.name.slice(0, 30) + '...'
              : survey?.name}
          </div>
          <div className="school-name">
            {survey?.schoolName?.length > 30
              ? survey?.schoolName?.slice(0, 30) + '...'
              : survey?.schoolName}
          </div>
        </Stack>
        <div>
          <Stack direction="row">
            {canDoActions &&
              survey.status !== 'Live' &&
              survey.status !== 'Closed' && (
                <IconButton
                  onClick={() =>
                    navigate(
                      paths.questionPanel.replace(
                        ':token',
                        encodeURIComponent(survey.token)
                      ),
                      {state: {survey: survey}}
                    )
                  }
                  disableRipple
                >
                  <Edit sx={{fontSize: '18px', marginRight: '10px'}} />
                </IconButton>
              )}
            {survey.status !== 'Closed' && (
              <IconButton onClick={handleClick} disableRipple>
                <MoreHoriz sx={{fontSize: '24px'}} />
              </IconButton>
            )}
            {canDoActions && (
              <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                className="options-menu"
              >
                {survey.status !== 'Live' && survey.status !== 'Closed' && (
                  <MenuItem
                    onClick={e => {
                      setAnchorEl(null)
                      setDeleteConfirm(true)
                    }}
                  >
                    Delete
                  </MenuItem>
                )}
                {survey.status === 'Live' && (
                  <>
                    <MenuItem
                      onClick={e => {
                        setAnchorEl(null)
                        setShowResendInvites(true)
                      }}
                    >
                      Resend Invites
                    </MenuItem>
                    <MenuItem
                      onClick={e => {
                        setAnchorEl(null)
                        setShowSurveyQuestions(true)
                      }}
                    >
                      View All Questions
                    </MenuItem>
                    {canChangeEndDate && (
                      <MenuItem
                        onClick={e => {
                          setAnchorEl(null)
                          setShowChangeEndDate(true)
                        }}
                      >
                        Change End Date
                      </MenuItem>
                    )}
                    {isMatSurvey && (
                      <MenuItem
                        onClick={e => {
                          setAnchorEl(null)
                          setShowAddSchool(true)
                        }}
                      >
                        Add School
                      </MenuItem>
                    )}
                  </>
                )}
              </Menu>
            )}
          </Stack>
        </div>
      </Stack>
      {showResendInvites && (
        <WbModal
          title="Resend Invites"
          onClose={e => setShowResendInvites(false)}
          content={<ResendInviteTabs survey={survey}></ResendInviteTabs>}
        ></WbModal>
      )}
      {showSurveyQuestions && (
        <WbModal
          title="View All Questions"
          onClose={e => setShowSurveyQuestions(false)}
          content={<SurveyQuestions survey={survey}></SurveyQuestions>}
        ></WbModal>
      )}
      {showChangeEndDate && (
        <WbModal
          title="Change End Date/Time"
          onClose={() => setShowChangeEndDate(false)}
          content={
            <ChangeEndDate
              survey={survey}
              onClose={() => setShowChangeEndDate(false)}
              onSuccess={loadSurveys}
            />
          }
        />
      )}
      {showAddSchool && (
        <WbModal
          title="Add School"
          onClose={() => setShowAddSchool(false)}
          content={
            <AddSchool
              survey={survey}
              onClose={() => setShowAddSchool(false)}
              onSuccess={loadSurveys}
            />
          }
        />
      )}
      <Divider orientation="horizontal" />

      <div>
        <Stack direction="row" mt={1} justifyContent="space-between">
          <div className="survey-date">
            {survey.status === 'Closed' ? (
              <Button
                className="analyse-data-btn"
                startIcon={<BarChart />}
                style={{textTransform: 'none'}}
                onClick={e =>
                  window.open(
                    eiSurveyUrl(survey.resultId),
                    '_blank'
                  )
                }
                disableElevation
                disableRipple
                disableFocusRipple
              >
                Analyse Data
              </Button>
            ) : survey.status === 'Live' ? (
              <Stack>
                <GroupCyclesList
                  token={survey.token}
                  loadSurveys={loadSurveys}
                  resultId={survey.resultId}
                />
                <ParticipantCount survey={survey} cType={cType} />
              </Stack>
            ) : (
              `Created on: ${new Date(Date.parse(survey.createdAt)).toDateString()}`
            )}
          </div>
          <div>
            <Chip
              label={survey.status}
              style={{
                borderColor: survey.status === 'Live' ? '#E84F6B' : '#007A7A',
                color: survey.status === 'Live' ? '#E84F6B' : '#007A7A',
              }}
              size="small"
              variant="outlined"
            />
          </div>
        </Stack>

        {survey?.ownerName !== null && survey?.ownerName !== 'Insights User' ? (
          <div className="created-by">
            <PersonOutlineSharpIcon></PersonOutlineSharpIcon>
            Created by: {survey?.ownerName}
          </div>
        ) : (
          ''
        )}

        <div className="survey-bottom">
          <Stack direction="row" columnGap={0.5} flexWrap="wrap" rowGap={0.5}>
            {survey.surveysTags?.map(
              x =>
                x && (
                  <Chip
                    label={getTag(x)}
                    variant="outlined"
                    size="small"
                    className="tag-chip"
                  />
                )
            )}
          </Stack>

          <Stack direction="row" spacing={0.5} mt={1}>
            {getAudienceChip(survey.audience)}
          </Stack>
        </div>
      </div>
    </div>
  )
}
